/**
 * CoSingery — 三步流程 hook（① 對話已完成 → ② 歌詞（可改）→ ③ 確認出歌）
 *
 * 責任：**所有網絡動作 ＋ 狀態**（`/song/lyrics`、`PATCH /song/:id/lyrics`、`/song/:id/render`、
 *   輪詢 `/song/status/:id`）；頁面只負責顯示（硬規則 A：頁面唔准寫底層邏輯）。
 *
 * ⚠️ 幾個**唔可以省**嘅位：
 *   · 輪詢：真 provider 係非同步 → **輪詢請求本身就係完成動作**（後端會下載音頻入 R2）
 *   · 每一步嘅錯誤都要**逐種講清楚**（`rules.md` B2／B5：唔准一句「發生錯誤」蓋過）
 *   · 三個動作共用 `busy`（防重複撳 → 防重複扣 30 credits）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ⭐ **身份（0006；`decisions/018`）**：由對話頁傳入
 *   · `sessionId` ＝ 一次對話（歌曲流程屬於佢）
 *   · `deviceId`  ＝ 一部機（擁有者檢查／個人 library）
 *   ⚠️ 換 `sessionId`（開新對話）→ **歌曲流程要 reset**（唔准帶住上一首歌嘅狀態）
 */

export interface SongDto {
  id: string;
  title: string;
  lyrics: string;
  moodTags: string[];
  status: string;
  error: string | null;
  audioKey: string | null;
  providerJobId?: string | null;
  edited?: boolean;
  /** ⭐ 0011（`decisions/030`）：語體 `spoken`（口語）／`cantopop`（流行曲書面） */
  lyricRegister?: string | null;
  /** ⭐ 0014（`decisions/034`）：風格 id ＋ 來源（`auto`／`user`；NULL ＝ 冇紀錄） */
  genre?: string | null;
  genreSource?: string | null;
  /** 版本清單（Mureka 一次回 2 個；`> 1` ＝ 用戶可以揀） */
  variants?: { index: number; durationMs: number | null; hasHifi: boolean }[];
  pickedIndex?: number;
}

/** `idle`＝未開始；`lyrics`＝等用戶檢視／改；`rendering`＝生成中；`done`＝有結果（成功或失敗） */
export type SongPhase = 'idle' | 'lyrics' | 'rendering' | 'done';

const PHASE_OF = (song: SongDto | null): SongPhase => {
  if (!song) return 'idle';
  if (song.status === 'lyrics_ready') return 'lyrics';
  if (song.status === 'running') return 'rendering';
  return 'done';
};

export function useSongFlow(opts: { locale: string; sessionId: string; deviceId: string }) {
  const { locale, sessionId, deviceId } = opts;
  const { t } = useTranslation();
  const [song, setSong] = useState<SongDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * ⭐ **温柔提示**（唔係錯誤）—— 現時只有 `need_more`（唔夠料寫歌）：
   *   刻意同 `error` 分開 → 頁面可以用**唔同顏色**顯示（唔用紅字／唔用 `role="alert"`）。
   *   依據：產品負責人 2026-09-21 指示（`decisions/015`）。
   */
  const [gentle, setGentle] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const phase = PHASE_OF(song);

  /**
   * ⭐ **開新對話 → reset 歌曲流程**（`decisions/018`）：唔准留住上一首歌嘅狀態
   *   （舊歌唔會消失 —— 佢仍然喺 `deviceId` 嘅個人 library 見到）
   */
  useEffect(() => {
    setSong(null);
    setError(null);
    setGentle(null);
    setSaved(false);
  }, [sessionId]);

  /** 錯誤碼 → 人話（**逐種講清楚**；唔准籠統） */
  const messageFor = useCallback(
    (code: string | undefined, ctx: { editing?: boolean } = {}): string => {
      switch (code) {
        case 'song_hold':
          return t('song_hold_notice');
        /** ⭐ 唔夠料（**唔係錯誤**）：温柔邀請繼續傾（`decisions/015`） */
        case 'need_more':
          return t('song_need_more_notice');
        case 'too_generic':
          return t('song_error_too_generic');
        case 'too_repetitive':
          return t('song_error_repetitive');
        case 'content_blocked':
          return ctx.editing ? t('song_edit_error_blocked') : t('song_error_blocked');
        case 'lyrics_too_short':
          return t('song_edit_error_short');
        case 'selfcheck_failed':
        case 'selfcheck_unavailable':
          return t('song_error_blocked');
        default:
          return `${t('song_error_generic')}${code ? `（${code}）` : ''}`;
      }
    },
    [t],
  );

  const request = useCallback(
    async (
      path: string,
      init: RequestInit,
      ctx: { editing?: boolean } = {},
    ): Promise<{ ok: boolean; song?: SongDto; code?: string; status?: number }> => {
      setBusy(true);
      setError(null);
      setGentle(null);
      try {
        const res = await fetch(path, init);
        const data = (await res.json().catch(() => ({}))) as {
          song?: SongDto;
          error?: string;
          turnsRemaining?: number;
          status?: string;
        };
        if (res.ok && data.song) {
          setSong(data.song);
          return { ok: true, song: data.song };
        }
        const msg =
          data.error === 'song_hold'
            ? `${messageFor('song_hold')}（+${data.turnsRemaining ?? 2}）`
            : messageFor(data.error, ctx);
        /**
         * ⭐ `need_more`（唔夠料寫歌）**唔係錯誤**（`decisions/015`）→ 走温柔提示（唔用紅字、
         *   唔用 `role="alert"`）；其他 code（含 `song_hold`）照舊當錯誤顯示。
         */
        if (data.error === 'need_more') setGentle(msg);
        else setError(msg);
        return { ok: false, code: data.error, status: res.status };
      } catch (err) {
        setError(`${t('song_error_generic')}（${String(err).slice(0, 80)}）`);
        return { ok: false, code: 'network_error' };
      } finally {
        setBusy(false);
      }
    },
    [messageFor, t],
  );

  /** ② 寫歌詞（**唔會花錢**）；可以帶「一定要有」嘅字句（Must-use box）＋ 歌詞語言 ＋ 語體 ＋ 曲風 */
  const writeLyrics = useCallback(
    async (
      mustUse?: { text: string; mode: 'exact' | 'meaning' }[],
      /** ⭐ 歌詞語言（粵／普／英）；唔傳 ＝ 跟介面 locale */
      songLanguage?: string,
      /** ⭐ 語體（`decisions/030`）：`cantopop`（預設）／`spoken` */
      lyricRegister?: string,
      /** ⭐ 曲風（`decisions/034`）：用戶揀嘅 slug；`null`／唔傳 ＝ 自動（AI 用 mood_tags 推導） */
      genre?: string | null,
    ) => {
      setSaved(false);
      await request('/api/song/lyrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          deviceId,
          locale,
          songLanguage: songLanguage ?? locale,
          /** ⚠️ 唔傳 ＝ 服務端用預設（`cantopop`；唔准前端寫死，兩邊唔可以各有預設） */
          ...(lyricRegister ? { lyricRegister } : {}),
          /** ⚠️ 只有用戶**真係揀咗**才傳；自動 ＝ 唔傳（由服務端推導＋記 `genre_source`） */
          ...(genre ? { genre } : {}),
          mustUse: mustUse ?? [],
        }),
      });
    },
    [locale, request, sessionId, deviceId],
  );

  /** ②b 儲存用戶改過嘅歌詞（守衛喺服務端；唔過會 422 原樣講） */
  const saveLyrics = useCallback(
    async (draft: { title: string; lyrics: string }) => {
      if (!song) return { ok: false };
      const res = await request(
        `/api/song/${song.id}/lyrics`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, deviceId, ...draft }),
        },
        { editing: true },
      );
      setSaved(res.ok);
      return res;
    },
    [request, song],
  );

  /** ③ 確定出歌（**唯一會花錢**嘅一步） */
  const confirmRender = useCallback(async () => {
    if (!song) return { ok: false };
    setSaved(false);
    return request(`/api/song/${song.id}/render`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, deviceId, locale }),
    });
  }, [locale, request, song]);

  /** ③b 揀版本（**唔會再花 provider 錢**；後端會按需要抓 flac 高音質） */
  const pickVariant = useCallback(
    async (index: number) => {
      if (!song) return { ok: false };
      return request(`/api/song/${song.id}/pick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, deviceId, index }),
      });
    },
    [request, song],
  );

  /** 輪詢（`running` 時每 5 秒一次；**呢個請求就係完成動作**） */
  useEffect(() => {
    if (song?.status !== 'running') return;
    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/song/status/${song.id}`);
        const data = (await res.json().catch(() => ({}))) as { song?: SongDto };
        if (alive && data.song) setSong(data.song);
      } catch {
        /** 輪詢失敗唔致命：下一輪再試（provider 仲喺生成） */
      }
    }, 5000);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [song]);

  return { song, busy, error, gentle, saved, phase, writeLyrics, saveLyrics, confirmRender, pickVariant };
}
